import { atom } from 'jotai'

export type ToastVariant = 'warn' | 'error' | 'success' | 'info'
export type ToastType = 'action' | 'simple' | 'promise'
export type PromiseToastStatus = 'pending' | 'success' | 'error'

// Base toast interface
interface BaseToast {
  id: string
  variant: ToastVariant
  type: ToastType
}

// Action toast with title, description, and confirm button
export interface ActionToast extends BaseToast {
  type: 'action'
  title: string
  description?: string
  confirmLabel?: string
  onConfirm?: () => void
}

// Simple toast with just a message
export interface SimpleToast extends BaseToast {
  type: 'simple'
  message: string
  duration?: number // auto dismiss after ms
}

// Promise toast - can't be dismissed until resolved
export interface PromiseToast extends BaseToast {
  type: 'promise'
  message: string
  status: PromiseToastStatus
  successMessage?: string
  errorMessage?: string
}

export type ChatToast = ActionToast | SimpleToast | PromiseToast

// Input types for adding toasts (without id)
export type NewActionToast = Omit<ActionToast, 'id'>
export type NewSimpleToast = Omit<SimpleToast, 'id'>
export type NewPromiseToast = Omit<PromiseToast, 'id'>
export type NewChatToast = NewActionToast | NewSimpleToast | NewPromiseToast

export const chatToastsAtom = atom<ChatToast[]>([])

// Derived atoms for actions
export const addChatToastAtom = atom(null, (get, set, toast: NewChatToast) => {
  const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
  const newToast: ChatToast = { ...toast, id } as ChatToast
  set(chatToastsAtom, [...get(chatToastsAtom), newToast])
  return id
})

export const removeChatToastAtom = atom(null, (get, set, id: string) => {
  set(
    chatToastsAtom,
    get(chatToastsAtom).filter((toast) => toast.id !== id)
  )
})

export const updatePromiseToastAtom = atom(
  null,
  (get, set, { id, status }: { id: string; status: PromiseToastStatus }) => {
    set(
      chatToastsAtom,
      get(chatToastsAtom).map((toast) => {
        if (toast.id === id && toast.type === 'promise') {
          return { ...toast, status }
        }
        return toast
      })
    )
  }
)

export const clearAllChatToastsAtom = atom(null, (_get, set) => {
  set(chatToastsAtom, [])
})
